import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { LoadingOutlined } from '@ant-design/icons';
import { Input, Tabs, Button, Empty, Select, Spin, Popover, notification } from 'antd';
import axios from 'axios';
import * as _ from 'lodash';
import TaskDetails from './TaskDetails';
import TaskHistory from './TaskHistory';
import TaskFiles from './TaskFiles';
import WorkflowAuth from './workflow-auth';
import ResponsiveImage from '../../ResponsiveImage'
import { Icon } from 'dlweb-components';

const WorkflowTask = ({hideModal, task, user, onComplete, clientId, tenantId, authType, readOnly}) => {

  let timer = useRef(null);
  let off = useRef(() => {});
  let iframeRef = useRef(null);

  const { TextArea } = Input;
  const { TabPane } = Tabs;
  const antIcon = <LoadingOutlined style={{ fontSize: 14, marginLeft: '5px' }} spin />;

  const [taskInfo, setTaskInfo] = useState({});
  const [fileId, setFileId] = useState('');
  const [filesRestricted, setFilesRestricted] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [textAreaValue, setTextareaValue] = useState('');
  const [decision, setDecision] = useState(null);
  const [decisionOptions, setDecisionOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customAction, setCustomAction] = useState({});
  const [notSupported, setNotSupported] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [visible, setVisible] = useState(false);
  const [badLoginCount, setBadLoginCount] = useState(0);
  const [countdownTimer, setCountdownTimer] = useState(-1);
  const [requireApprovalPassword, setRequireApprovalPassword] = useState(false);

  useEffect(() => {
    if (countdownTimer > 0) {
      timer.current = setTimeout(() => setCountdownTimer(c => c - 1), 1000);
    }
    else {
      setEnabled(true);
    }
    return () => clearTimeout(timer.current)
  }, [countdownTimer])

  const padTime = time => {
    return String(time).length === 1 ? `0${time}` : `${time}`;
  };

  const formatTimer = time => {
    // Convert seconds into minutes and take the whole part
    const minutes = Math.floor(time / 60);

    // Get the seconds left after converting minutes
    const seconds = time % 60;

    //Return combined values as string in format mm:ss
    return `${minutes}:${padTime(seconds)}`;
  };

  const angularScope = angular.element('#container').scope();

  useEffect(() => {

    if (_.isEmpty(task)) return;
    
    const approveTypes = [
      'Approve By All',
      'Approve By One',
      'Final Approve By All',
      'Final Approve By One'
    ]

    const yesNoTypes = [
      'YesNo By All',
      'YesNo By One'
    ]

    const completeTypes = [
      'Edit By All',
      'Edit By One',
      'Review By All',
      'Review By One',
      'Client Action By All',
      'Client Action By One',
      'Client Action By Team Member'
    ]

    const dropdownTypes = [
      'Multiple Value By All',
      'Multiple Value By One'
    ]


    axios.post('/api/wt/workflow/dto/step/read', { ...task }).then(
      res => {

        if(!_.isEmpty(res.data.errors)) {
          notification.error({ message: res.data.errors[0] });
          hideModal();
          onComplete();
        }

        const data = res.data.data;
        const st = data.stepType;

        setFilesRestricted(data.filesRestricted);

        setRequireApprovalPassword(data.requireApprovalPassword == 1);

        if(data.stepUrl || data.requiredReading) {

          // handle URL custom action
          data.stepUrl && setCustomAction({type: 'url', data: data.stepUrl});

          // handle Required Reading custom action
          if(data.requiredReading) {
            setEnabled(false);
            setCustomAction({type: 'requiredReading'});
          }
        }

        if(_.includes(approveTypes, st)) {
          setDecisionOptions([
            {
              text: 'Approve',
              value: 1
            },
            {
              text: 'Decline',
              value: 0
            }
          ]);
        }
        else if(_.includes(yesNoTypes, st)) {
          setDecisionOptions([
            {
              text: 'Yes',
              value: 1
            },
            {
              text: 'No',
              value: 0
            }
          ]);
        }
        else if(_.includes(completeTypes, st)) {
          setDecisionOptions([
            {
              text: 'Task Complete',
              value: 1
            }
          ]);
        }
        else if(_.includes(dropdownTypes, st)) {
          setDecisionOptions(data.values.map(opt => ({ text: opt, value: opt})));
        }
        else {
          setNotSupported(true);
        }

        setTaskInfo(data);
      }
    ).catch(error => {
      alert(`Error loading task.`);
      console.log(error);
    });
  }, [task, hideModal, onComplete])

  const openPreview = useCallback(() => {
    angularScope.$emit('loadObjectPreview', {id: fileId});
  }, [fileId, angularScope])

  useEffect(() => {
    if(!_.isEmpty(fileId)) {

      off.current = angularScope.$on('previewLoaded', (event, data) => {

        if(taskInfo.requiredReading) {

          const { pageInterval, maxTimeout } = taskInfo;
          const pagesTimer = data.pageCount * pageInterval;
          const upperBound = maxTimeout > 0 ? maxTimeout : pagesTimer;

          const finalTimer = _.clamp(pagesTimer, pageInterval, upperBound);

          setCustomAction({...customAction, ...data})
          setCountdownTimer(finalTimer);
        }

        if (data && data.isImage) {
          window.document.body.querySelector('.image-preview-img').addEventListener('dblclick', openPreview)
        }
        else {
          iframeRef.current && iframeRef.current.contentWindow.document.body.querySelector('#viewerContainer').addEventListener('dblclick', openPreview)
        }

        off.current();
      })

      return () => {
        window.document.body.querySelector('.image-preview-img')?.removeEventListener('dblclick', openPreview);
        iframeRef.current && iframeRef.current.contentWindow.document.body.querySelector('#viewerContainer')?.addEventListener('dblclick', openPreview)
      }
    }
  }, [fileId, angularScope, openPreview, taskInfo, enabled, customAction])

  const onFileSelected = useCallback((id) => {
    axios.get(`/api/preview/getpreview?id=${id}`).then((res) => {
      setFileId(id);
        setPreviewData({ ...res.data, nocache: Date.now(), path: `${res.data.path}`});
    }).catch((error) => {
      console.error(error);
    })
  }, [setFileId])

  const processStep = useCallback(() => {
    const postData = {
      instanceId: task.InstanceID,
      stepNumber: task.StepNumber,
      routeId: task.RouteID,
      value: decision,
      comment: textAreaValue
    }

    axios.post(`api/wt/workflow/dto/process`, postData).then((res) => {

      if(!_.isEmpty(res.data.errors)) {
        notification.error({ message: "Could not complete workflow step." });
        console.error(res.data.errors[0]);
        setLoading(false);
      }
      else {
        _.isFunction(onComplete) && onComplete();
        notification.success({ message: "Workflow step completed." });
        hideModal();
      }
    }).catch((err) => {
      console.error(err);
      notification.error({ message: "Error processing workflow step." });
      setLoading(false);
    })
  }, [decision, onComplete, task, textAreaValue, hideModal])

  const checkAuth = useCallback((data) => axios.post('/api/wt/workflow/authenticate', data).then((res) => {
    if(res.data.data) {
      processStep();
    }
    else {
      setBadLoginCount((n) => {
        const count = n + 1;

        if(count === 3) {
          hideModal();
        }else {
          return count;
        }
      })

      showLoginError(res.data.errors[0]);
      setLoading(false);
    }
  }), [hideModal, processStep]);

  const showLoginError = (error) => {
    notification.error({ message: error });
  }

  const onSubmit = useCallback(() => {

    setLoading(true);

    if(requireApprovalPassword) {
      switch (authType) {
        case 'oidc':
          window.dl.launchOIDC(true, clientId, tenantId).then((data) => {
            checkAuth({ email: data.idTokenClaims.preferred_username })
          }).catch(() => setLoading(false));
          break;
        case 'saml':
          window.dl.launchSAML(true, true).then((userEmail) => {
            checkAuth({ email: userEmail })
          }).catch(() => setLoading(false));
          break;
        default:
          setVisible(true);
          break;
      }
    }
    else {
      processStep();
    }

  }, [authType, clientId, requireApprovalPassword, tenantId, checkAuth, processStep])

  const onAuthSubmit = (user, pass) => {
    setVisible(false);
    checkAuth({ userId: user, password: pass });
  }

  const openLegacyWorkflowDialog = () => {
    const locationService = window.angular.element('#container').injector().get('$location');
    locationService.url(`/?WFRow=${task.InstanceID}&WFStep=${task.StepNumber}&WFRoute=${task.RouteID}`);
    angularScope.$apply();
  }

  const onCustomActionClick = () => {
    taskInfo.stepUrl && window.open(taskInfo.stepUrl, '_blank');
  }

  return (
    <Container customAction={customAction}>
      <MainContent>
        <FormContainer>
          <TabContainer customAction={customAction}>
            <Tabs
                type="card"
                animated={false}
                defaultActiveKey="1"
                style={{height: '100%'}}
                tabBarStyle={{ width: '100%', }}
                destroyInactiveTabPane={false}
                items={[
                  {
                    label: <div>
                              <Icon type='chalkboard-teacher' size="lg" style={{marginRight: '10px'}}/>
                                Instructions
                            </div>,
                    key: '1',
                    children: (
                      <div style={{padding: '15px' }}>
                        {(taskInfo.instructions || taskInfo.requestMessage) &&
                          <>
                            <InfoText>{_.unescape(taskInfo.requestMessage)}</InfoText>
                            <InfoText>{_.unescape(taskInfo.instructions)}</InfoText>
                          </>
                        }
                        {!taskInfo.instructions && !taskInfo.requestMessage &&
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No instructions found"/>
                        }
                      </div>
                    ),
                  },
                  {
                    label: <div>
                              <Icon type='info-circle' size="lg" style={{marginRight: '10px'}}/>
                                Details
                            </div>,
                    key: '2',
                    children: (
                          <TabPane tab="Details" key="details">
                            <TaskDetails task={task}/>
                          </TabPane>),
                  },
                  {
                    label: <div>
                              <Icon type='history' size="lg" style={{marginRight: '10px'}}/>
                                History
                            </div>,
                    key: '3',
                    children: <TabPane tab="History" key="history">
                                <TaskHistory task={task}/>
                              </TabPane>,
                  }
                ]}
              >
              </Tabs>
            </TabContainer>
            {!_.isEmpty(filesRestricted) &&
              <ErrorContainer>
                <strong>You do not have permission to approve these files:</strong>
                {filesRestricted.map((item, i) => (
                    <div key={i} style={{margin: '5px 0'}}>{item}</div>
                  ))
                }
              </ErrorContainer>
            }
            {_.isEmpty(filesRestricted) &&
            <TextAreaContainer>
                Comments:
                <TextArea
                  rows={4}
                  style={{resize: 'none'}}
                  value={textAreaValue}
                  onChange={e => setTextareaValue(e.target.value)}
                />
              </TextAreaContainer>
            }
            <DecisionContainer>
              <div style={{textAlign: 'right'}}>
                {!_.isEmpty(customAction) &&
                  <>
                    {customAction.type === 'url' &&
                      <CustomActionContainer>
                        <div style={{float: 'left', fontWeight: 'bold', lineHeight: '32px'}}>Action:</div>
                        <Button onClick={onCustomActionClick} type="primary" style={{marginLeft: '35px'}}>Launch URL</Button>
                      </CustomActionContainer>
                    }
                    {customAction.type === 'requiredReading' &&
                      <CustomActionContainer>
                        <div style={{float: 'left', fontWeight: 'bold'}}>Action:</div>
                          <div style={{marginLeft: '35px', float: 'left', width: '295px', marginBottom: '10px'}}>
                            Minimum time remaining for review: {countdownTimer === -1 ? <Spin indicator={antIcon} style={{lineHeight: '10px'}}/> : formatTimer(countdownTimer)}
                          </div>
                      </CustomActionContainer>
                    }
                  </>
                }
                {!_.isEmpty(taskInfo) && !notSupported &&
                  <>
                    <div style={{float: 'left', lineHeight: '32px', fontWeight: 'bold'}}>Decision:</div>
                    <Select style={{width: '300px', textAlign: 'left'}} onSelect={value => setDecision(value) } disabled={!enabled || !_.isEmpty(filesRestricted)}>
                      {decisionOptions.map(item => (
                          <Select.Option key={item.value} value={item.value}>
                            {item.text}
                          </Select.Option>
                        ))
                      }
                    </Select>
                  </>
                }
                {notSupported &&
                  <div style={{textAlign: 'left', padding: '5px 0', color: '#000'}}>
                    You must open this task in the Workflow view to complete it.
                  </div>
                }
                </div>
            </DecisionContainer>
            <ButtonContainer>
              <Button
                style={{width: '100px', marginRight: '10px'}}
                onClick={hideModal}
              >
                Cancel
              </Button>
              {!notSupported &&
                <Popover
                  content={<WorkflowAuth onClose={() => { setVisible(false); setLoading(false); }}
                  onSubmit={onAuthSubmit}/>}
                  open={visible}
                  zIndex={9999999}
                  title="Authentication Required"
                >
                  <Button
                    style={{width: '100px'}}
                    type="primary"
                    disabled={_.isNull(decision)}
                    loading={loading}
                    onClick={() => onSubmit(true)}
                  >
                    Submit
                  </Button>
                </Popover>
              }
              {notSupported &&
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => openLegacyWorkflowDialog()}
                >
                  Open in Worflow view
                </Button>
              }
            </ButtonContainer>
        </FormContainer>
        <FilesContainer>
          {!_.isEmpty(taskInfo) &&
            <PreviewContainer>
              <TaskFiles
                task={task}
                user={user}
                readOnly={readOnly}
                hideModal={hideModal}
                onFileSelected={onFileSelected}
              />
              {previewData?.path && !previewData?.isImage &&
                <iframe ref={iframeRef} style={{ height: '100%' }} src={`/lib/pdf.js/web/viewer.html?file=${previewData.path}&nocache=${previewData.nocache}`} />
              }
              {previewData?.path && previewData?.isImage &&
                <div style={{position: 'relative', width: '100%', height: '100%'}}>
                  <ResponsiveImage
                    imagePath={decodeURIComponent(previewData.path)}
                    angularScope={angularScope}
                  />
                </div>
              }
            </PreviewContainer>
          }
        </FilesContainer>
      </MainContent>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  height: 100%;
  .ant-form-item {
    margin-bottom: 0;
  }
  .ant-tabs-nav {
    width: 400px !important;
    text-align: center;
  }
  .ant-tabs-nav div {
    flex: 1;
  }
  .ant-tabs-tab {
    flex: 1;
    justify-content: center;
  }
  .ant-tabs-tabpane {
    height: calc(100% - 40px);
    overflow-x: auto;
    user-select: text;
  }
  .ant-tabs-nav-container {
    width: 400px;
  }
  textarea {
    border-color: #dbdbdb !important;
  }
  .ant-tabs-nav .ant-tabs-tab::before,
  .ant-tabs.ant-tabs-card .ant-tabs-card-bar .ant-tabs-tab-active::before {
    display: none;
  }
  .ant-tabs.ant-tabs-card .ant-tabs-card-bar .ant-tabs-tab {
    border-top: none;
  }
  .ant-select-selector {
    border-radius: 6px 0 0 6px;
  }
`

const MainContent = styled.div`
  position: relative;
  height: 100%;
  padding-bottom: 10px;
  overflow: hidden;
  display: flex;
`

const FormContainer = styled.div`
  flex: 0 0 400px;
  display: flex;
  overflow: hidden;
  flex-direction: column;
`

const TabContainer = styled.div`
  flex: 1 0;
  overflow: hidden;
`

const FilesContainer = styled.div`
  height: 100%;
  flex: 1 1;
  margin: 0 0 15px 25px;
  background: #f8f8f8;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
`

const PreviewContainer = styled.div`
 margin-bottom: 20px;
 height: calc(100% - 50px);
`

const TextAreaContainer = styled.div`
  margin-top: 15px;
`

const ErrorContainer = styled.div`
  margin-top: 15px;
  height: 120px;
  overflow: auto;
  padding: 10px;
  background-color: #fef8f9;
  border: 1px solid var(--error-color);
`

const DecisionContainer = styled.div`
  margin: 15px 0;
  padding: 10px;
  border-radius: 4px;
  background-color: #efefef;
  display: grid;
`

const CustomActionContainer = styled.div`
  width: 100%;
  margin-bottom: 8px;
  text-align: left;
`

const ButtonContainer = styled.div`
  width: 100%;
  text-align: right;
  display: inline-block;
`

const InfoText = styled.pre`
  overflow: hidden;
  white-space: pre-wrap;
  font-family: inherit;
`
const mapStateToProps = state => {
  const { authType, readOnly } = state.user;
  const { clientId, tenantId } = state.app;
  return { authType, readOnly, clientId, tenantId };
};

export default connect(mapStateToProps)(WorkflowTask);